import React from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import {
    DocumentTextIcon,
    PencilSquareIcon,
    TrashIcon,
    ArrowRightIcon,
} from '@heroicons/react/24/outline';

/**
 * My Briefs card (bd deepcontext-736j locked mockup): tinted nav-active icon
 * square with an accent-ink document glyph, bold truncated name, ghost round
 * trash button top-right, 4-line-clamped ink-soft preview, ghost tag pills,
 * and an outline "Open in Universe" pill. Cards hover-lift 2px with a soft
 * shadow (subtle, user-action-adjacent motion — hover is a direct response
 * to cursor position, not an ambient loop).
 */

export interface Brief {
    id: string;
    company_id: string;
    name: string;
    brief_text: string;
    tags: string[];
    created_by_user_id: string | null;
    created_at?: string;
    updated_at?: string;
}

interface BriefCardProps {
    brief: Brief;
    onOpenInClusters: (brief: Brief) => void;
    onEdit: (brief: Brief) => void;
    onDelete: (brief: Brief) => void;
}

const BriefCard: React.FC<BriefCardProps> = ({ brief, onOpenInClusters, onEdit, onDelete }) => {
    const preview = brief.brief_text.length > 180
        ? `${brief.brief_text.slice(0, 180).trim()}…`
        : brief.brief_text;

    return (
        <Card className="flex flex-col h-full border border-line transition-transform duration-150 hover:-translate-y-0.5 hover:shadow-lg">
            <CardContent className="flex-1 flex flex-col gap-3">
                <div
                    data-testid={`brief-card-${brief.id}`}
                    className="flex items-center gap-2"
                >
                    <div className="w-[30px] h-[30px] rounded-[10px] bg-nav-active flex items-center justify-center flex-shrink-0">
                        <DocumentTextIcon className="h-4 w-4 text-accent-active" />
                    </div>
                    <h3
                        className="text-sm font-extrabold text-ink truncate min-w-0"
                        title={brief.name}
                    >
                        {brief.name}
                    </h3>
                    <div className="ml-auto flex-shrink-0 flex items-center gap-1">
                        <button
                            type="button"
                            onClick={() => onEdit(brief)}
                            className="w-6 h-6 rounded-full flex items-center justify-center text-ink-muted hover:bg-ghost-bg transition-colors"
                            aria-label={`Edit brief ${brief.name}`}
                            data-testid={`brief-card-edit-${brief.id}`}
                        >
                            <PencilSquareIcon className="h-3.5 w-3.5" />
                        </button>
                        <button
                            type="button"
                            onClick={() => onDelete(brief)}
                            className="w-6 h-6 rounded-full flex items-center justify-center text-ink-muted hover:bg-ghost-bg transition-colors"
                            aria-label={`Delete brief ${brief.name}`}
                            data-testid={`brief-card-delete-${brief.id}`}
                        >
                            <TrashIcon className="h-3.5 w-3.5" />
                        </button>
                    </div>
                </div>

                <p className="text-xs text-ink-muted whitespace-pre-wrap line-clamp-4 leading-relaxed flex-1">
                    {preview}
                </p>

                {brief.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                        {brief.tags.map((tag) => (
                            <span
                                key={tag}
                                className="inline-flex items-center rounded-full bg-ghost-bg px-2.5 py-0.5 text-xs font-bold text-ink-muted"
                            >
                                {tag}
                            </span>
                        ))}
                    </div>
                )}

                <div className="mt-auto">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onOpenInClusters(brief)}
                        data-testid={`brief-card-open-${brief.id}`}
                    >
                        Open in Universe
                        <ArrowRightIcon className="h-4 w-4 ml-2" />
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
};

export default BriefCard;
